import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'rsuite';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import ParagraphText from 'components/Elements/ParagraphText/ParagraphText';
import Button from 'components/Elements/Button/Button';
import TextFormField from 'components/Form/Input/Base/TextFormField';
import PhoneNumberFormField from 'components/Form/Input/Base/PhoneNumberFormField';
import SelectFormField from 'components/Form/Input/Base/SelectFormField';
import useSchoolList from 'features/schools/hooks/useSchoolList';
import { PICKER_ETHNICITY } from 'constants/picker';
import useSignup from '../../hooks/useSignUp';
import styles from './SignUpForm.module.scss';

/** Same standing options as Profile / office check-in. */
const GRADE_PICKER = [
  ...['6', '7', '8', '9', '10', '11', '12'].map(value => ({
    label: `${value}th Grade`,
    value,
  })),
  { label: 'College Freshman', value: 'freshman' },
  { label: 'College Sophomore', value: 'sophomore' },
  { label: 'College Junior', value: 'junior' },
  { label: 'College Senior', value: 'senior' },
  { label: 'Graduated', value: 'graduated' },
  { label: 'Not in school', value: 'Not in school' },
  { label: 'Professional', value: 'professional' },
];

const GRADE_FIELD_DATA = {
  picker: GRADE_PICKER,
  searchable: false,
};

const ETHNICITY_FIELD_DATA = {
  picker: PICKER_ETHNICITY,
  otherLabel: 'Enter your ethnicity',
  searchable: false,
};

type SignUpFormProps = {
  onEmailModeChange?: (isEmailMode: boolean) => void;
};

function SignUpForm({ onEmailModeChange = undefined }: SignUpFormProps) {
  const [linkFormValue, setLinkFormValue] = useState<{ linkPassword: string }>({
    linkPassword: '',
  });
  const { schoolOptions, knownSchoolValues, loading: schoolsLoading } = useSchoolList();

  const schoolFieldData = useMemo(
    () => ({
      picker: [...schoolOptions, { label: 'Other', value: 'other' }],
      otherLabel: 'School name',
      searchable: true,
    }),
    [schoolOptions],
  );

  const {
    model,
    formValue,
    formError,
    isLoading,
    onSubmit,
    onChange,
    onGoogleSignUp,
    pendingGoogleEmail,
    completeLinking,
    cancelLinking,
    formRef,
    hasPrefill,
    reconcilePrefillSchool,
  } = useSignup();
  const [showEmailForm, setShowEmailForm] = useState(hasPrefill);

  // Invite prefill arrives after the first render.
  useEffect(() => {
    if (!hasPrefill) return;
    setShowEmailForm(true);
    onEmailModeChange?.(true);
  }, [hasPrefill, onEmailModeChange]);

  useEffect(() => {
    if (schoolsLoading) return;
    reconcilePrefillSchool(knownSchoolValues, schoolOptions);
  }, [
    schoolsLoading,
    knownSchoolValues,
    schoolOptions,
    reconcilePrefillSchool,
    hasPrefill,
  ]);

  const openEmailForm = () => {
    setShowEmailForm(true);
    onEmailModeChange?.(true);
  };

  return (
    <div className={styles.container}>
      {pendingGoogleEmail && (
        <Form
          fluid
          formValue={linkFormValue}
          onChange={v => setLinkFormValue(v as { linkPassword: string })}
        >
          <div className={styles.emailModeHeader}>
            <button
              className={styles.textLinkButton}
              type="button"
              onClick={cancelLinking}
            >
              Cancel
            </button>
          </div>

          <ParagraphText marginBottom={16}>
            {`An account with ${pendingGoogleEmail} already exists. Enter your password to link your Google account.`}
          </ParagraphText>

          <TextFormField
            required
            size="lg"
            type="password"
            name="linkPassword"
            label="Password"
          />

          <Button
            size="full"
            type="button"
            isLoading={isLoading}
            onClick={() => completeLinking(linkFormValue.linkPassword)}
          >
            <ParagraphText textAlign="center" margin={0} color="#fff">
              Link &amp; Sign Up
            </ParagraphText>
          </Button>
        </Form>
      )}

      {!pendingGoogleEmail && !showEmailForm && (
        <div className={styles.authChoiceContainer}>
          <Button
            className={styles.darkGoogleButton}
            size="full"
            type="button"
            isLoading={isLoading}
            onClick={onGoogleSignUp}
            startIcon={<FontAwesomeIcon color="#fff" icon={faGoogle} />}
          >
            <ParagraphText textAlign="center" margin={0} color="#fff">
              Sign up with Google
            </ParagraphText>
          </Button>

          <Button
            size="full"
            type="button"
            color="blue"
            onClick={openEmailForm}
          >
            <ParagraphText textAlign="center" margin={0} color="#fff">
              Sign up with Email
            </ParagraphText>
          </Button>

          <div className={styles.linksContainer}>
            <NavLink className={styles.customLink} to="/">
              Already have an account? Sign in
            </NavLink>
          </div>
        </div>
      )}

      {!pendingGoogleEmail && showEmailForm && (
        <Form
          fluid
          ref={formRef}
          model={model}
          formValue={formValue}
          formError={formError}
          onChange={val => onChange(val as typeof formValue)}
          onCheck={errors => {
            if (errors && Object.keys(errors).length === 0) {
              // no-op — keep rsuite client validation in sync
            }
          }}
        >
          <TextFormField
            required
            size="lg"
            type="text"
            name="firstName"
            label="First name"
          />
          <TextFormField
            required
            size="lg"
            type="text"
            name="lastName"
            label="Last name"
          />

          <SelectFormField
            required
            size="lg"
            name="school"
            label="School"
            formValue={formValue}
            data={schoolFieldData}
          />

          <SelectFormField
            required
            size="lg"
            name="grade"
            label="Grade level"
            formValue={formValue}
            data={GRADE_FIELD_DATA}
          />

          <SelectFormField
            size="lg"
            name="ethnicity"
            label="Ethnicity"
            formValue={formValue}
            data={ETHNICITY_FIELD_DATA}
          />

          <TextFormField
            required
            size="lg"
            type="email"
            name="email"
            label="Email"
          />

          <PhoneNumberFormField
            required
            size="lg"
            name="phoneNumber"
            label="Phone number"
          />

          <TextFormField
            required
            size="lg"
            type="password"
            name="password"
            label="Password"
          />

          <TextFormField
            required
            size="lg"
            type="password"
            name="verifyPassword"
            label="Confirm password"
          />

          <Button
            size="full"
            type="button"
            isLoading={isLoading}
            disabled={isLoading}
            onClick={onSubmit}
          >
            <ParagraphText textAlign="center" margin={0} color="#fff">
              {isLoading ? 'Creating account...' : 'Create account'}
            </ParagraphText>
          </Button>

          <div className={styles.linksContainer}>
            <NavLink className={styles.customLink} to="/">
              Already have an account? Sign in
            </NavLink>
          </div>
        </Form>
      )}
    </div>
  );
}

export default SignUpForm;
